/**
 * Rate Limiter & Request Queue for Gemini API calls.
 *
 * Prevents rate limiting on long trips (7+ days) by controlling
 * concurrent API requests and adding delays between bursts.
 */

// --- Request Counting ---
let totalApiCalls = 0;
let tripGenerationCalls = 0;
let tripGenerationStartTime = 0;

export function resetTripCounter(): void {
  tripGenerationCalls = 0;
  tripGenerationStartTime = Date.now();
}

export function logTripSummary(): void {
  const elapsed = ((Date.now() - tripGenerationStartTime) / 1000).toFixed(1);
}

function incrementCallCount(): void {
  totalApiCalls++;
  tripGenerationCalls++;
}

// --- Priority Queue Item ---
interface QueueItem<T = any> {
  fn: () => Promise<T>;
  priority: number;
  resolve: (value: T) => void;
  reject: (error: any) => void;
}

// --- RequestQueue Class ---
export class RequestQueue {
  private queue: QueueItem[] = [];
  private activeCount = 0;
  private readonly maxConcurrent: number;
  private readonly delayBetweenMs: number;
  private readonly name: string;
  private lastRequestTime = 0;

  constructor(maxConcurrent: number = 3, delayBetweenMs: number = 200, name: string = 'default') {
    this.maxConcurrent = maxConcurrent;
    this.delayBetweenMs = delayBetweenMs;
    this.name = name;
  }

  /**
   * Add a task to the queue. Returns a promise that resolves with the task's result.
   * Lower priority numbers execute first (0 = highest priority).
   */
  async add<T>(fn: () => Promise<T>, priority: number = 10): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const item: QueueItem<T> = { fn, priority, resolve, reject };

      // Insert in priority order (lower number = higher priority)
      const insertIndex = this.queue.findIndex(q => q.priority > priority);
      if (insertIndex === -1) {
        this.queue.push(item);
      } else {
        this.queue.splice(insertIndex, 0, item);
      }

      this.logStatus();
      this.processNext();
    });
  }

  private async processNext(): Promise<void> {
    if (this.activeCount >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    const item = this.queue.shift()!;
    this.activeCount++;

    // Enforce minimum delay between requests
    const now = Date.now();
    const elapsed = now - this.lastRequestTime;
    if (elapsed < this.delayBetweenMs && this.lastRequestTime > 0) {
      await new Promise(resolve => setTimeout(resolve, this.delayBetweenMs - elapsed));
    }
    this.lastRequestTime = Date.now();

    try {
      incrementCallCount();
      const timeoutMs = 45000; // 45 second max per task (was 120s — reduced for faster failure feedback)
      const result = await Promise.race([
        item.fn(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Task timeout')), timeoutMs)
        )
      ]);
      item.resolve(result);
    } catch (error) {
      item.reject(error);
    } finally {
      this.activeCount--;
      this.logStatus();
      // Process next item after this one completes
      this.processNext();
    }
  }

  private logStatus(): void {
    if (this.queue.length > 0 || this.activeCount > 0) {
    }
  }

  get pending(): number {
    return this.queue.length;
  }

  get active(): number {
    return this.activeCount;
  }
}

// --- Singleton Queue Instances ---
// Images: increased concurrency for faster generation (was 4)
export const imageQueue = new RequestQueue(8, 50, 'image');

// Text generation (day itineraries, skeleton, suggestions): increased for parallel days (was 5)
export const textQueue = new RequestQueue(15, 30, 'text');

// Google Search grounding calls: increased to reduce queue wait (was 6/100ms)
export const searchQueue = new RequestQueue(12, 50, 'search');

// --- throttledPromiseAll ---
/**
 * Drop-in replacement for Promise.all that limits concurrency.
 * Unlike Promise.all which fires everything at once, this executes
 * tasks in batches with delays between them.
 *
 * Results are returned in the original order regardless of completion order.
 *
 * @param tasks - Array of functions that return promises (NOT promises themselves)
 * @param maxConcurrent - Maximum number of tasks to run simultaneously
 * @param delayMs - Minimum delay between starting new tasks
 */
export async function throttledPromiseAll<T>(
  tasks: Array<() => Promise<T>>,
  maxConcurrent: number = 3,
  delayMs: number = 200
): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let nextIndex = 0;
  let completedCount = 0;

  const totalTasks = tasks.length;

  return new Promise<T[]>((resolve, reject) => {
    let hasRejected = false;

    async function runNext(): Promise<void> {
      if (hasRejected) return;

      const index = nextIndex++;
      if (index >= totalTasks) return;

      // Add delay between launches (except the first batch)
      if (index >= maxConcurrent) {
        await new Promise(r => setTimeout(r, delayMs));
      }

      try {
        // Note: no incrementCallCount() here — the underlying queue.add() calls handle counting
        results[index] = await tasks[index]();
        completedCount++;

        if (completedCount % 3 === 0 || completedCount === totalTasks) {
        }

        if (completedCount === totalTasks) {
          resolve(results);
        } else {
          runNext();
        }
      } catch (error) {
        if (!hasRejected) {
          hasRejected = true;
          reject(error);
        }
      }
    }

    // Launch initial batch
    const initialBatch = Math.min(maxConcurrent, totalTasks);
    for (let i = 0; i < initialBatch; i++) {
      runNext();
    }

    // Handle empty tasks array
    if (totalTasks === 0) {
      resolve([]);
    }
  });
}
